import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { checkVersion, handleChunkError } from './components/versionCheck';
import './assets/css/general.css';
import './App.css';

// Lazy load components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const QualityPage = lazy(() => import('./pages/QualityPage'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ProductsPage = lazy(() => import('./products/Products'));
const ProductDetailPage = lazy(() => import('./products/ProductDetailPage'));
const RequestQuotePage = lazy(() => import('./products/RequestQuotePage'));
const NotFound = lazy(() => import('./pages/NotFound'));

/**
 * A component to handle language changes from the URL.
 * It also uses <Outlet /> to render the matched child route,
 * which is the key to making nested routing work.
 */
const LanguageHandler = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    // List of supported languages
    const supportedLanguages = ['en', 'de'];
    if (lang && supportedLanguages.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // The Outlet component from react-router-dom renders the child route's element
  return <Outlet />;
};

/**
 * Layout component that includes Header and Footer
 * This ensures consistent layout for all routes
 */
const Layout = () => {
  return (
    <>
      <Header />
      <Suspense fallback={<LoadingSpinner />}>
        <Outlet />
      </Suspense>
      <Footer />
    </>
  );
};

function App() {
  useEffect(() => {
    // Check version on mount to detect stale cached versions
    checkVersion();

    // Global error handler for chunk loading errors
    const errorHandler = (event) => {
      const error = event.error || event.reason;
      if (handleChunkError(error)) {
        event.preventDefault();
      }
    };

    // Listen for both synchronous and asynchronous errors
    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', errorHandler);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', errorHandler);
    };
  }, []);

  return (
    <Router>
      <HelmetProvider>
        <ErrorBoundary>
          <Routes>
            {/* --- Root routes (English - no language prefix) --- */}
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="quality" element={<QualityPage />} />
              <Route path="industries" element={<IndustriesPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:id" element={<ProductDetailPage />} />
              <Route path="request-quote" element={<RequestQuotePage />} />
            </Route>

            {/* --- Routes with language prefix (German and other languages) --- */}
            <Route path="/:lang" element={<LanguageHandler />}>
              <Route path="" element={<Layout />}>
                {/* The 'index' route matches the parent's path exactly (e.g., /de) */}
                <Route index element={<Home />} />
                
                {/* Child routes use relative paths. They will be appended to the parent's path. */}
                <Route path="about" element={<About />} />
                <Route path="quality" element={<QualityPage />} />
                <Route path="industries" element={<IndustriesPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/:id" element={<ProductDetailPage />} />
                <Route path="request-quote" element={<RequestQuotePage />} />
              </Route>
            </Route>

            {/* --- 404 Page --- */}
            {/* This will catch any route that hasn't been matched above */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </HelmetProvider>
    </Router>
  );
}

export default App;